import React, { useState, useMemo, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useGetCallFlowsQuery, useAssignCallFlowToPhoneNumberMutation } from "@/store/rest_api";
import PhoneNumber from "@/model/interface/PhoneNumber";
import { CallFlow } from "@/model/interface/CallFlow";
import { Skeleton } from "@/components/ui/skeleton";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { toast } from "sonner";
import { apiErrorMessage } from "@/util/apiError";

interface AssignCallFlowDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  phoneNumber: PhoneNumber;
  assignedFlow?: CallFlow;
}

export function AssignCallFlowDialog({ open, onOpenChange, phoneNumber, assignedFlow }: AssignCallFlowDialogProps) {
  const navigate = useNavigate();
  const { data: callFlows, isLoading } = useGetCallFlowsQuery();
  const [assignCallFlow, { isLoading: isUpdating }] = useAssignCallFlowToPhoneNumberMutation();
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedFlowId, setSelectedFlowId] = useState<string | null>(assignedFlow?.id || null);

  const filteredFlows = useMemo(() => {
    if (!callFlows) return [];
    if (!searchQuery.trim()) return callFlows;

    const query = searchQuery.toLowerCase();
    return callFlows.filter(
      (flow) => flow.name.toLowerCase().includes(query) || flow.description?.toLowerCase().includes(query),
    );
  }, [callFlows, searchQuery]);

  const handleAssign = async () => {
    try {
      await assignCallFlow({
        id: phoneNumber.id,
        callFlowId: selectedFlowId || null,
      }).unwrap();

      toast.success(selectedFlowId ? "Call flow assigned successfully" : "Call flow removed successfully");
      onOpenChange(false);
    } catch (error) {
      toast.error(apiErrorMessage(error, "Failed to assign call flow"));
    }
  };

  useEffect(() => {
    if (open) {
      setSelectedFlowId(assignedFlow?.id ?? null);
    }
  }, [open, assignedFlow?.id]);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[500px]">
        <DialogHeader>
          <DialogTitle>{assignedFlow ? "Change Call Flow" : "Assign Call Flow"}</DialogTitle>
          <DialogDescription>
            Assign a call flow to {phoneNumber.number}. Incoming calls will follow this flow.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-4">
          <div className="space-y-2">
            <Label htmlFor="search">Search Call Flows</Label>
            <Input
              id="search"
              placeholder="Search by name or description..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
            />
          </div>

          <div className="space-y-2">
            <Label>Select Call Flow</Label>
            {isLoading ? (
              <div className="space-y-2">
                <Skeleton className="h-10 w-full" />
                <Skeleton className="h-10 w-full" />
                <Skeleton className="h-10 w-full" />
              </div>
            ) : (
              <RadioGroup value={selectedFlowId || ""} onValueChange={setSelectedFlowId}>
                <div className="flex items-center space-x-2 border rounded-md p-3 hover:bg-muted">
                  <RadioGroupItem value="" id="none" />
                  <Label htmlFor="none" className="flex-1 cursor-pointer">
                    <div className="font-medium">No Call Flow</div>
                    <div className="text-sm text-muted-foreground">Remove call flow assignment</div>
                  </Label>
                </div>
                {filteredFlows.length > 0 ? (
                  <div className="max-h-[300px] overflow-y-auto space-y-2">
                    {filteredFlows.map((flow) => (
                      <div key={flow.id} className="flex items-center space-x-2 border rounded-md p-3 hover:bg-muted">
                        <RadioGroupItem value={flow.id} id={flow.id} />
                        <Label htmlFor={flow.id} className="flex-1 cursor-pointer">
                          <div className="font-medium">{flow.name}</div>
                          {flow.description && <div className="text-sm text-muted-foreground">{flow.description}</div>}
                        </Label>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-8 text-muted-foreground">
                    No call flows found matching your search.
                  </div>
                )}
              </RadioGroup>
            )}
          </div>
        </div>

        <DialogFooter className="sm:justify-between">
          <Button variant="outline" onClick={() => navigate("/manage-organisation/call-flows/create")}>
            New Call Flow
          </Button>
          <div className="flex gap-2">
            <Button variant="outline" onClick={() => onOpenChange(false)} disabled={isUpdating}>
              Cancel
            </Button>
            <Button onClick={handleAssign} disabled={isUpdating}>
              {isUpdating ? "Saving..." : assignedFlow ? "Change Flow" : "Assign Flow"}
            </Button>
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
